
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { trackEvent } from '@/services/analytics';

const BUCKET_NAME = 'chat-attachments';

/**
 * Uploads a file to the chat attachments bucket in Supabase storage
 * @param file The file to upload
 * @param storeId The ID of the store where the chat is happening
 * @returns URL to the uploaded attachment
 */
export const uploadChatAttachment = async (file: File, storeId: string): Promise<string> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${storeId}/${uuidv4()}.${fileExt}`;
    const filePath = `${fileName}`;

    // Track upload attempt
    trackEvent('chat_attachment_upload_started', {
      file_type: file.type,
      file_size: file.size,
      store_id: storeId
    });

    // Upload the file to Supabase storage
    const { data, error } = await supabase
      .storage
      .from(BUCKET_NAME)
      .upload(filePath, file);

    if (error) {
      console.error('Error uploading chat attachment:', error);
      trackEvent('chat_attachment_upload_failed', {
        file_type: file.type,
        error: error.message
      });
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase
      .storage
      .from(BUCKET_NAME)
      .getPublicUrl(data.path);

    // Track successful upload
    trackEvent('chat_attachment_upload_succeeded', {
      file_type: file.type,
      file_size: file.size,
      store_id: storeId
    });

    return publicUrl;
  } catch (error) {
    console.error('Error in uploadChatAttachment:', error);
    throw error;
  }
};

/**
 * Removes a chat attachment from storage
 * @param url The public URL of the attachment to delete
 */
export const removeChatAttachment = async (url: string): Promise<boolean> => {
  try {
    // Extract the file path from the URL
    const urlParts = url.split('/');
    const bucketIndex = urlParts.findIndex(part => part === BUCKET_NAME);
    
    if (bucketIndex === -1) {
      console.error('Invalid attachment URL format:', url);
      return false;
    }
    
    const filePath = urlParts.slice(bucketIndex + 1).join('/');
    
    const { error } = await supabase
      .storage
      .from(BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error('Error removing chat attachment:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in removeChatAttachment:', error);
    return false;
  }
};
